import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type { GeologyType } from '~/apollo/generated/v4/graphql';
import { Tooltip } from '~/components/common/Tooltip';
import { useKPPanelContext } from '~/components/key-parameters/key-parameters-panel-v4/kp-panel-context';
import { geologyWikiPageRoute } from '~/paths';
import { cn } from '~/utils/common';

export function LinkedDepositionalValue({
  geologyType,
  type,
  value,
  dominant = false,
  children,
}: {
  geologyType: GeologyType;
  type: string;
  value: string | null | undefined;
  dominant?: boolean | null;
  children: ReactNode;
}) {
  const { depositionalWikiList } = useKPPanelContext();

  if (!value) return null;

  const wikiPage = (() => {
    const wp = depositionalWikiList.find(wikiPage => {
      const wikiPageValues = wikiPage.value ?? [];
      return (
        wikiPage.geologyType === geologyType.toLowerCase() &&
        wikiPage.type === type &&
        wikiPageValues.includes(value)
      );
    });

    if (wp) return wp;

    // If a wiki page wasn't found with the given geology type, look again for any GT
    return depositionalWikiList.find(wikiPage => {
      const wikiPageValues = wikiPage.value ?? [];
      return wikiPage.type === type && wikiPageValues.includes(value);
    });
  })();

  const tooltip = 'Dominant Key Parameter';

  if (!wikiPage) {
    return (
      <Tooltip message={tooltip} disabled={!dominant} className="inline">
        <span
          className={
            dominant
              ? 'font-bold border-b-2 border-b-yellow-300 rounded-xs cursor-default'
              : undefined
          }
        >
          {children}
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip message={tooltip} disabled={!dominant} className="inline">
      <Link
        to={geologyWikiPageRoute(parseInt(wikiPage.id))}
        target="_blank"
        className={cn('link', {
          'font-bold border-b-2 border-b-yellow-300 hover:border-b-yellow-500 rounded-xs cursor-pointer':
            dominant,
        })}
      >
        {children}
      </Link>
    </Tooltip>
  );
}
